// use-hl-recent-trades — 4s-Poll von /info `recentTrades` für Live-Trades-Tape.
// V1 hatte 4s, behalten wir bei. Re-poll bei Coin-Wechsel.

import { useEffect, useState } from 'react';
import { fetchRecentTrades, type RecentTrade } from '@/lib/hl-info-api';

const POLL_INTERVAL_MS = 4_000;
const TRADES_LIMIT = 30;

export interface UseHlRecentTradesOptions {
  enabled?: boolean;
  pollIntervalMs?: number;
  limit?: number;
}

export interface UseHlRecentTradesReturn {
  trades: RecentTrade[];
  loading: boolean;
  refetch: () => Promise<void>;
}

export function useHlRecentTrades(
  coin: string,
  options: UseHlRecentTradesOptions = {},
): UseHlRecentTradesReturn {
  const { enabled = true, pollIntervalMs = POLL_INTERVAL_MS, limit = TRADES_LIMIT } = options;
  const [trades, setTrades] = useState<RecentTrade[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !coin) {
      setTrades([]);
      return undefined;
    }
    let cancelled = false;

    const poll = async (): Promise<void> => {
      if (cancelled) return;
      setLoading(true);
      try {
        const next = await fetchRecentTrades(coin, limit);
        if (!cancelled) setTrades(next);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void poll();
    const id = setInterval(() => void poll(), pollIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [enabled, coin, pollIntervalMs, limit]);

  const refetch = async (): Promise<void> => {
    if (!coin) return;
    setLoading(true);
    try {
      const next = await fetchRecentTrades(coin, limit);
      setTrades(next);
    } finally {
      setLoading(false);
    }
  };

  return { trades, loading, refetch };
}
